import { httpsCallable } from 'firebase/functions';
import type {
  CreateUploadRequest,
  CreateUploadResponse,
  FinalizeUploadRequest,
  FinalizeUploadResponse,
  MediaRole,
  UploadTarget,
} from '../../../shared/types';
import { functions } from '../firebase';
import { blurOf, prepareImage, smallOf } from './image';
import { inspectVideo } from './video';
import { baseType } from './audio';
import type { Leg } from '../uploadLegs';

const createUpload = httpsCallable<CreateUploadRequest, CreateUploadResponse>(
  functions,
  'createUpload',
);
const finalizeUpload = httpsCallable<FinalizeUploadRequest, FinalizeUploadResponse>(
  functions,
  'finalizeUpload',
);

export interface UploadHandle {
  promise: Promise<FinalizeUploadResponse>;
  cancel: () => void;
}

/** The last two steps of an upload, written down before they are taken: see ../uploadLegs. */
export type LegDraft = Omit<Leg, 'jobId' | 'idToken' | 'leaseUntil'>;

/**
 * Called with the leg once the ticket is in hand, before any bytes go: the
 * queue writes it down so the service worker can finish it if the app closes.
 */
export type BeforeSend = (draft: LegDraft) => Promise<void>;

/**
 * The bytes, then the finalize, from a ticket already in hand: the end of
 * every upload, and how one handed on and not yet finished is picked up again.
 */
export function sendLeg(draft: LegDraft, onProgress: (fraction: number) => void): UploadHandle {
  let request: XMLHttpRequest | undefined;
  const promise = (async () => {
    // First, and never a reason to stop: it is a tenth the size of the photo.
    if (draft.small) await put(draft.small, draft.small.body, () => undefined).catch(() => undefined);
    request = await put(draft, draft.body, onProgress);
    try {
      return (await finalizeUpload(draft.finalize)).data;
    } catch (err) {
      // The service worker finished it first: the message is written.
      if ((err as { code?: string }).code === 'functions/failed-precondition') return { status: 'ready' as const };
      throw err;
    }
  })();
  return { promise, cancel: () => request?.abort() };
}

/** From a fresh ticket: written down for the worker first, if asked, then sent. */
async function finish(
  ticket: CreateUploadResponse,
  body: Blob,
  finalize: FinalizeUploadRequest,
  onProgress: (fraction: number) => void,
  beforeSend: BeforeSend | undefined,
  hold: (handle: UploadHandle) => void,
  small?: Blob | null,
): Promise<FinalizeUploadResponse> {
  const draft: LegDraft = {
    uploadId: ticket.uploadId,
    url: ticket.url,
    headers: ticket.headers,
    expiresAt: ticket.expiresAt,
    body,
    // Only where the server made room for it.
    ...(small && ticket.small ? { small: { ...ticket.small, body: small } } : {}),
    finalize,
  };
  await beforeSend?.(draft);
  const handle = sendLeg(draft, onProgress);
  hold(handle);
  return handle.promise;
}

/**
 * Three steps: ask the server for permission and a presigned URL, PUT the
 * bytes straight to R2, then tell the server it landed.
 *
 * The bytes never pass through a function, which is what keeps this inside the
 * free tier, and the presigned URL is scoped to one key with the content type
 * and length pinned, so it cannot be reused for anything else.
 */
export function uploadPhoto(
  file: File,
  target: UploadTarget,
  onProgress: (fraction: number) => void,
  role?: MediaRole,
  beforeSend?: BeforeSend,
): UploadHandle {
  let sending: UploadHandle | undefined;

  const promise = (async () => {
    const prepared = await prepareImage(file);
    // Nice to have, never a reason for the photo not to go: whatever goes
    // wrong making it, even at once, the photo goes up without one.
    const blur = await Promise.resolve()
      .then(() => blurOf(prepared.blob, prepared.width, prepared.height))
      .catch(() => undefined);
    // The same: drawn whole wherever there is none. A snap is only opened whole.
    const small =
      target.kind === 'snap'
        ? null
        : await Promise.resolve()
            .then(() => smallOf(prepared.blob, prepared.width, prepared.height))
            .catch(() => null);

    const ticket = await createUpload({
      kind: 'photo',
      contentType: 'image/jpeg',
      bytes: prepared.blob.size,
      target,
      ...(role ? { role } : {}),
      ...(small ? { smallBytes: small.size } : {}),
    });

    return finish(
      ticket.data,
      prepared.blob,
      { uploadId: ticket.data.uploadId, width: prepared.width, height: prepared.height, ...(blur ? { blur } : {}) },
      onProgress,
      beforeSend,
      (handle) => (sending = handle),
      small,
    );
  })();

  return { promise, cancel: () => sending?.cancel() };
}

/**
 * Anything that is not a photo, a clip or a recording. Uploaded exactly as it
 * was chosen: no re-encoding, no dimensions, and none of prepareImage — there
 * is nothing to look at, so there is nothing to prepare.
 */
export function uploadFile(
  file: File,
  target: UploadTarget,
  onProgress: (fraction: number) => void,
  beforeSend?: BeforeSend,
): UploadHandle {
  let sending: UploadHandle | undefined;

  const promise = (async () => {
    const ticket = await createUpload({
      kind: 'file',
      // A browser leaves this empty for a type it does not know, and the
      // server wants something shaped like a media type either way.
      contentType: file.type || 'application/octet-stream',
      bytes: file.size,
      name: file.name,
      target,
    });

    return finish(
      ticket.data,
      file,
      { uploadId: ticket.data.uploadId, width: 0, height: 0 },
      onProgress,
      beforeSend,
      (handle) => (sending = handle),
    );
  })();

  return { promise, cancel: () => sending?.cancel() };
}

export function uploadVideo(
  file: File,
  target: UploadTarget,
  onProgress: (fraction: number) => void,
  role?: MediaRole,
  beforeSend?: BeforeSend,
): UploadHandle {
  let sending: UploadHandle | undefined;

  const promise = (async () => {
    const facts = await inspectVideo(file);

    const ticket = await createUpload({
      kind: 'video',
      contentType: file.type || 'video/mp4',
      bytes: file.size,
      durationSec: facts.durationSec,
      target,
      ...(role ? { role } : {}),
    });

    return finish(
      ticket.data,
      file,
      {
        uploadId: ticket.data.uploadId,
        width: facts.width,
        height: facts.height,
        durationSec: facts.durationSec,
      },
      onProgress,
      beforeSend,
      (handle) => (sending = handle),
    );
  })();

  return { promise, cancel: () => sending?.cancel() };
}

/**
 * A voice note. It uploads in whatever container the browser recorded, and
 * transcodeAudio turns it into the m4a everyone can play; the message shows a
 * processing state in between, exactly as a video does.
 */
export function uploadVoice(
  blob: Blob,
  durationSec: number,
  target: UploadTarget,
  onProgress: (fraction: number) => void,
  beforeSend?: BeforeSend,
): UploadHandle {
  let sending: UploadHandle | undefined;

  const promise = (async () => {
    const ticket = await createUpload({
      kind: 'audio',
      contentType: baseType(blob.type),
      bytes: blob.size,
      durationSec,
      target,
    });

    return finish(
      ticket.data,
      blob,
      // A recording has no picture, so there is nothing to measure.
      { uploadId: ticket.data.uploadId, width: 0, height: 0, durationSec },
      onProgress,
      beforeSend,
      (handle) => (sending = handle),
    );
  })();

  return { promise, cancel: () => sending?.cancel() };
}

/**
 * XHR rather than fetch, only because fetch still cannot report upload
 * progress and a 90-second video on family wifi needs a progress bar.
 */
function put(
  ticket: Pick<CreateUploadResponse, 'url' | 'headers'>,
  body: Blob,
  onProgress: (fraction: number) => void,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', ticket.url, true);
    for (const [key, value] of Object.entries(ticket.headers)) {
      // The browser sets Content-Length itself and rejects attempts to set it.
      if (key.toLowerCase() === 'content-length') continue;
      xhr.setRequestHeader(key, value);
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve(xhr);
      } else {
        reject(new Error(`upload failed with ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('upload failed: network'));
    xhr.onabort = () => reject(new Error('upload cancelled'));

    xhr.send(body);
  });
}
